const isAnswerNodeType = (nodeType) => String(nodeType || '').toLowerCase() === 'answer';

const isAnswerVariableSelector = (selector) => {
  if (!Array.isArray(selector) || selector.length === 0) return false;
  return selector.some((part) => String(part).toLowerCase().includes('answer'));
};

const pickAnswerNodeText = (outputs = {}) => {
  if (outputs.answer) return String(outputs.answer);
  const text = outputs.text || outputs.output || '';
  return typeof text === 'string' && text.trim() ? text : '';
};

/**
 * 创建带状态的 SSE 解析器。
 * Chatflow 仅采集 Answer 节点输出；普通 Chat/Agent 保持原有行为。
 */
export const createStreamAnswerExtractor = () => {
  let inChatflow = false;
  let answerNodeActive = false;
  const answerNodeIds = new Set();

  const rememberAnswerNode = (nodeData = {}) => {
    const nodeId = nodeData.node_id || nodeData.id;
    if (nodeId != null) {
      answerNodeIds.add(String(nodeId));
    }
  };

  const extractStreamAnswer = (data) => {
    if (!data) return '';

    if (data.workflow_run_id) {
      inChatflow = true;
    }

    if (data.event === 'node_started') {
      const nodeData = data.data || {};
      if (isAnswerNodeType(nodeData.node_type)) {
        rememberAnswerNode(nodeData);
        answerNodeActive = true;
      } else {
        answerNodeActive = false;
      }
      return '';
    }

    if (data.event === 'node_finished') {
      const nodeData = data.data || {};
      if (!isAnswerNodeType(nodeData.node_type)) {
        answerNodeActive = false;
        return '';
      }
      rememberAnswerNode(nodeData);
      answerNodeActive = false;
      return pickAnswerNodeText(nodeData.outputs || {});
    }

    if (data.event === 'text_chunk') {
      const text = data.data?.text || data.text || '';
      if (!text) return '';

      if (!inChatflow) return text;

      const selector = data.data?.from_variable_selector;
      if (answerNodeActive || isAnswerVariableSelector(selector)) {
        return text;
      }

      const sourceId = Array.isArray(selector) ? String(selector[0] || '') : '';
      if (sourceId && answerNodeIds.has(sourceId)) {
        return text;
      }

      return '';
    }

    if (data.event === 'message' || data.event === 'agent_message') {
      const answer = data.answer || '';
      if (!answer) return '';

      if (!inChatflow) return answer;

      if (answerNodeActive) return answer;
      if (isAnswerVariableSelector(data.from_variable_selector)) return answer;
      return '';
    }

    if (data.event === 'workflow_finished') {
      return pickAnswerNodeText(data.data?.outputs || {});
    }

    return '';
  };

  const reset = () => {
    inChatflow = false;
    answerNodeActive = false;
    answerNodeIds.clear();
  };

  return { extractStreamAnswer, reset };
};

/** @deprecated 请使用 createStreamAnswerExtractor；保留给无状态单次解析 */
export const extractStreamAnswer = (data) => {
  if (!data) return '';

  if (data.event === 'text_chunk') {
    return data.data?.text || data.text || '';
  }

  if (data.event === 'message' || data.event === 'agent_message') {
    return data.answer || '';
  }

  if (data.event === 'node_finished') {
    const nodeData = data.data || {};
    if (!isAnswerNodeType(nodeData.node_type)) return '';
    return pickAnswerNodeText(nodeData.outputs || {});
  }

  if (data.event === 'workflow_finished') {
    return pickAnswerNodeText(data.data?.outputs || {});
  }

  return '';
};

/** 合并流式文本：增量追加 vs 整段替换 */
export const mergeStreamChunk = (current, chunk, data) => {
  if (!chunk) return current;

  if (data.event === 'text_chunk') {
    return `${current}${chunk}`;
  }

  if (data.event === 'message' || data.event === 'agent_message') {
    // Chatflow 常一次性下发完整 answer；标准 chat 为增量 delta
    if (Array.isArray(data.from_variable_selector)) return chunk;
    if (chunk.startsWith(current) && chunk.length > current.length) return chunk;
    if (!current) return chunk;
    if (chunk.length > 40 && current && !chunk.includes(current)) return chunk;
    return `${current}${chunk}`;
  }

  // node_finished / workflow_finished：整段替换
  if (chunk.length >= current.length || !current) return chunk;
  return current;
};

export const isStreamingDelta = (data) => data.event === 'text_chunk';

export const estimateTypingDelay = (contentLength) => {
  if (!contentLength) return 0;
  return Math.min(2500, Math.max(600, contentLength * 14));
};

/** 估算 StreamText 逐字显示完成所需时间 */
export const estimateStreamRevealMs = (contentLength) => {
  if (!contentLength) return 0;

  let revealed = 0;
  let ticks = 0;
  while (revealed < contentLength) {
    const remaining = contentLength - revealed;
    const step = remaining > 200
      ? Math.max(2, Math.ceil(remaining / 40))
      : remaining > 80
        ? Math.max(2, Math.ceil(remaining / 24))
        : remaining > 24
          ? Math.max(1, Math.ceil(remaining / 10))
          : 1;
    revealed += step;
    ticks += 1;
  }

  return Math.min(6000, ticks * 24 + 160);
};

export const generateTempMessageId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).slice(2, 15);
  const microtime = performance.now().toString().replace('.', '');
  return `temp_msg_${timestamp}_${random}_${microtime}`;
};

export const upsertAssistantStreamMessage = (
  prev,
  { content, displayConversationId, thinking, isRealtime },
  generateId = generateTempMessageId,
) => {
  const idx = prev.findIndex(
    (msg) => msg.role === 'assistant' && (msg.isThinking || msg.isRealtime),
  );
  const base = {
    role: 'assistant',
    created_at: new Date().toISOString(),
    conversation_id: displayConversationId,
    isRealtime: isRealtime !== undefined ? isRealtime : !thinking,
    isThinking: thinking,
  };
  if (idx !== -1) {
    return prev.map((msg, i) => (i === idx ? { ...msg, ...base, content } : msg));
  }
  return [
    ...prev,
    {
      ...base,
      id: generateId(),
      content,
    },
  ];
};

/** RAF 批量刷新流式内容 */
export const createStreamFlusher = (setMessages, upsertFn, onFlush) => {
  let pending = null;
  let rafId = null;

  const flush = () => {
    rafId = null;
    if (!pending) return;
    const payload = pending;
    pending = null;
    setMessages((prev) => upsertFn(prev, payload));
    onFlush?.(payload.content);
  };

  return {
    push(payload) {
      pending = payload;
      if (!rafId) {
        rafId = requestAnimationFrame(flush);
      }
    },
    flushNow(payload) {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      pending = null;
      setMessages((prev) => upsertFn(prev, payload));
      onFlush?.(payload.content);
    },
    cancel() {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      pending = null;
    },
  };
};

/** 离线回放 SSE 事件序列（小程序等） */
export const buildStreamSnapshots = (events) => {
  const { extractStreamAnswer } = createStreamAnswerExtractor();
  let content = '';
  const snapshots = [];
  let conversationId = null;
  let usage = null;
  let errored = null;

  (events || []).forEach((data) => {
    if (data.event === 'error' && data.error) {
      errored = typeof data.error === 'string' ? data.error : (data.error.message || 'AI回复失败');
      return;
    }

    const chunk = extractStreamAnswer(data);
    if (chunk) {
      const nextContent = mergeStreamChunk(content, chunk, data);
      if (nextContent !== content) {
        content = nextContent;
        snapshots.push({
          content,
          delay: isStreamingDelta(data) ? 20 : 36,
        });
      }
    }

    if (data.conversation_id) {
      conversationId = data.conversation_id;
    }

    if (data.event === 'message_end') {
      if (data.answer) {
        const nextContent = mergeStreamChunk(content, data.answer, data);
        if (nextContent !== content) {
          content = nextContent;
          snapshots.push({ content, delay: 24 });
        }
      }
      usage = data.usage || null;
    }
  });

  return {
    snapshots,
    finalContent: content,
    conversationId,
    usage,
    errored,
  };
};
